import { useEffect, useState } from 'react';

// Game clock helpers. A clock is just an "ends at" ISO timestamp stored on the
// game doc; remaining time is derived from it (the same approach as the demo
// session countdown in demo-session.ts → getDemoTimeRemaining). Set-and-run:
// there is no pause state to track, so the end timestamp is the whole model.

/** Milliseconds left until `endsAt` (clamped at 0). */
export function clockRemainingMs(endsAt: string | null | undefined): number {
  if (!endsAt) return 0;
  return Math.max(0, new Date(endsAt).getTime() - Date.now());
}

/**
 * Format remaining time. Under an hour → "M:SS" (e.g. "12:05"); an hour or more
 * → "H:MM:SS" (e.g. "1:44:14"). Expired → "0:00".
 */
export function formatClockRemaining(endsAt: string | null | undefined): string {
  const totalSec = Math.floor(clockRemainingMs(endsAt) / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface ClockState {
  /** Formatted M:SS string, ticking every second. */
  display: string;
  /** Whether the clock has hit 0:00. */
  isExpired: boolean;
  /** Whether a clock is set at all. */
  isRunning: boolean;
}

/**
 * Live-ticking clock readout. Re-renders once per second while a clock is set.
 * Pass `null`/undefined when no clock is active (the interval is then idle).
 */
export function useClockRemaining(endsAt: string | null | undefined): ClockState {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!endsAt) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [endsAt]);

  return {
    display: formatClockRemaining(endsAt),
    isExpired: !!endsAt && clockRemainingMs(endsAt) <= 0,
    isRunning: !!endsAt,
  };
}
